// src/views/renegociacoes/RenegociacoesList.jsx
import React, { useState, useEffect, useCallback } from "react";
import renegociacoesService    from "../../services/renegociacoesService";
import cobrancasService        from "../../services/cobrancasService";
import condominiosService      from "../../services/condominiosService";
import unidadesService         from "../../services/unidadesService";
import contasBancariasService  from "../../services/contasBancariasService";
import { useAuth }             from "../../contexts/AuthContext";

const fmtM = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtD = d => { try { return new Date((String(d || "").split("T")[0]) + "T12:00:00").toLocaleDateString("pt-BR"); } catch { return d || "—"; } };

const STATUS_RENEG = {
  Ativo:     { bg: "#DBEAFE", color: "#1E40AF", label: "Ativo" },
  Quitado:   { bg: "#DCFCE7", color: "#166534", label: "Quitado" },
  Quebrado:  { bg: "#FEE2E2", color: "#991B1B", label: "Quebrado" },
  Cancelado: { bg: "#F1F5F9", color:"var(--gray-400)", label: "Cancelado" },
};
const STATUS_PARC = {
  Aberto:    { bg: "#FEF9C3", color: "#854D0E" },
  Pago:      { bg: "#DCFCE7", color: "#166534" },
  Vencido:   { bg: "#FEE2E2", color: "#991B1B" },
  Cancelado: { bg: "#F1F5F9", color:"var(--gray-400)" },
};

const STEPS = ["Unidade", "Boletos", "Valores", "Preview", "Confirmar"];

// Próximo dia 10 (mês atual se ainda não passou, senão mês seguinte)
function proximoDia10() {
  const hj  = new Date();
  const d10 = new Date(hj.getFullYear(), hj.getMonth(), 10);
  if (d10 < hj) d10.setMonth(d10.getMonth() + 1);
  return d10.toISOString().split("T")[0];
}

// ── Banner ──────────────────────────────────────────────────────────────
function Banner({ tipo = "info", children, onClose }) {
  const cores = {
    error:   { bg: "#FEE2E2", border: "#FECACA", color: "#991B1B", icon: "bi-exclamation-octagon" },
    success: { bg: "#DCFCE7", border: "#BBF7D0", color: "#166534", icon: "bi-check-circle" },
    info:    { bg: "#EFF6FF", border: "#BFDBFE", color: "#1D4ED8", icon: "bi-info-circle" },
    warning: { bg: "#FEF3C7", border: "#FDE68A", color: "#92400E", icon: "bi-exclamation-triangle" },
  };
  const c = cores[tipo] || cores.info;
  if (!children) return null;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.color,
      borderRadius: 8, padding: "10px 14px", marginBottom: 12,
      display: "flex", alignItems: "center", gap: 10, fontSize: 13.5,
    }}>
      <i className={`bi ${c.icon}`} style={{ fontSize: 18 }} />
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button className="btn btn-sm" style={{ padding: "2px 8px", background: "transparent", color: c.color }} onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

// ── Card de resumo ──────────────────────────────────────────────────────
function StatCard({ label, value, color, bg, hint }) {
  return (
    <div style={{ background: bg, borderRadius: 10, padding: "12px 14px", border: `1px solid ${color}22` }}>
      <div style={{ fontSize: 11, fontWeight: 600, color, opacity: 0.85, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
      {hint && <div style={{ fontSize: 10.5, color:"var(--gray-500)", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

export default function RenegociacoesList() {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("renegociacoes");

  const [aba, setAba]                   = useState("lista");
  const [condominios, setCondominios]   = useState([]);
  const [idCondo, setIdCondo]           = useState("");
  const [lista, setLista]               = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);
  const [filtros, setFiltros]           = useState({ status: "", idUnidade: "" });
  const [detalhe, setDetalhe]           = useState(null);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [stats, setStats]               = useState(null);

  // Feedback (substitui alert/prompt)
  const [pageError, setPageError]     = useState("");
  const [pageSuccess, setPageSuccess] = useState("");

  // Modais
  const [showCancelar, setShowCancelar] = useState(null); // { id, ... }
  const [motivoCancel, setMotivoCancel] = useState("");
  const [cancelando, setCancelando]     = useState(false);

  // Wizard de criação
  const [step, setStep]                 = useState(0);
  const [unidades, setUnidades]         = useState([]);
  const [contas, setContas]             = useState([]);
  const [idUnidadeSel, setIdUnidadeSel] = useState("");
  const [boletosDisp, setBoletosDisp]   = useState([]);
  const [loadingBoletos, setLoadingBoletos] = useState(false);
  const [boletosSel, setBoletosSel]     = useState([]);
  const [formValores, setFormValores]   = useState({
    desconto: "0.00", nrParcelas: "1",
    dtPrimeiraParcela: proximoDia10(),
    idContaBancaria: "", observacao: "",
  });
  const [simulacao, setSimulacao]   = useState(null);
  const [loadingSim, setLoadingSim] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  // Auto-dismiss success após 5s
  useEffect(() => {
    if (!pageSuccess) return;
    const t = setTimeout(() => setPageSuccess(""), 5000);
    return () => clearTimeout(t);
  }, [pageSuccess]);

  // Sync com condomínio ativo da navbar
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  useEffect(() => {
    condominiosService.listar({ limit: 100 })
      .then(r => setCondominios(r.data || []))
      .catch(e => setPageError(e?.message || "Não foi possível carregar a lista de condomínios."));
  }, []);

  useEffect(() => {
    if (!idCondo) return;
    unidadesService.listar({ idCondominio: idCondo, limit: 500 })
      .then(r => setUnidades(r.data || []))
      .catch(() => {});
    contasBancariasService.listar({ idCondominio: idCondo, situacao: "ativo" })
      .then(r => {
        setContas(r.data || []);
        if (r.data?.length > 0) setFormValores(p => ({ ...p, idContaBancaria: String(r.data[0].id) }));
      })
      .catch(() => {});
    // atualizarVencidas é executado pelo job diário no backend.
  }, [idCondo]);

  const carregarLista = useCallback(async () => {
    if (!idCondo) return;
    setLoadingLista(true);
    try {
      const r = await renegociacoesService.listar({ idCondominio: idCondo, ...filtros, limit: 50 });
      setLista(r.data || []);
    } catch (e) {
      setPageError(e?.message || "Não foi possível carregar as renegociações.");
    } finally { setLoadingLista(false); }
  }, [idCondo, filtros]);

  const carregarStats = useCallback(async () => {
    if (!idCondo) return;
    try {
      const r = await renegociacoesService.stats({ idCondominio: idCondo });
      setStats(r.data || null);
    } catch { /* ignora — cards só somem */ }
  }, [idCondo]);

  useEffect(() => {
    if (aba === "lista") { carregarLista(); carregarStats(); }
  }, [aba, carregarLista, carregarStats]);

  // Step 0 → 1: carrega boletos disponíveis da unidade
  const carregarBoletos = async () => {
    if (!idUnidadeSel) { setPageError("Selecione uma unidade."); return; }
    setLoadingBoletos(true); setBoletosSel([]); setBoletosDisp([]);
    try {
      const r = await renegociacoesService.boletosDisponiveis({ idCondominio: idCondo, idUnidade: idUnidadeSel });
      setBoletosDisp(r.data || []);
      if (r.sugestao?.dtPrimeiraParcela) {
        setFormValores(p => ({ ...p, dtPrimeiraParcela: r.sugestao.dtPrimeiraParcela }));
      }
      if (!r.data?.length) {
        setPageSuccess("Nenhum boleto vencido disponível para esta unidade — a unidade está em dia.");
      }
    } catch (e) {
      setPageError(e?.message || "Falha ao buscar boletos vencidos.");
    } finally { setLoadingBoletos(false); }
  };

  const toggleBoleto = (id) => setBoletosSel(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleTodos  = () => setBoletosSel(boletosSel.length === boletosDisp.length ? [] : boletosDisp.map(b => b.id));

  // Step 2 → 3: simula acordo
  const handleSimular = async () => {
    if (!boletosSel.length) { setPageError("Selecione ao menos um boleto."); return; }
    setLoadingSim(true); setPageError("");
    try {
      const r = await renegociacoesService.simular({
        idBoletos: boletosSel, idCondominio: idCondo,
        desconto: formValores.desconto,
        nrParcelas: formValores.nrParcelas,
        dtPrimeiraParcela: formValores.dtPrimeiraParcela,
      });
      setSimulacao(r.simulacao); setStep(3);
    } catch (e) {
      setPageError(e?.message || "Falha ao simular o acordo.");
    } finally { setLoadingSim(false); }
  };

  // Confirma o acordo
  const handleConfirmar = async () => {
    setConfirmando(true); setPageError("");
    try {
      const unidade = unidades.find(u => String(u.id) === String(idUnidadeSel));
      const r = await renegociacoesService.confirmar({
        idCondominio: idCondo, idUnidade: idUnidadeSel,
        idCondomino: unidade?.idCondomino || null,
        idContaBancaria: formValores.idContaBancaria,
        idBoletos: boletosSel,
        desconto: formValores.desconto,
        nrParcelas: formValores.nrParcelas,
        dtPrimeiraParcela: formValores.dtPrimeiraParcela,
        observacao: formValores.observacao,
      });
      setPageSuccess(r?.message || "Renegociação criada com sucesso.");
      // Reset wizard
      setStep(0); setIdUnidadeSel(""); setBoletosSel([]); setSimulacao(null);
      setFormValores({
        desconto: "0.00", nrParcelas: "1",
        dtPrimeiraParcela: proximoDia10(),
        idContaBancaria: contas[0] ? String(contas[0].id) : "",
        observacao: "",
      });
      setAba("lista");
      await Promise.all([carregarLista(), carregarStats()]);
    } catch (e) {
      setPageError(e?.message || "Falha ao confirmar o acordo.");
    } finally { setConfirmando(false); }
  };

  const handleVerDetalhe = async (r) => {
    if (detalhe?.id === r.id) { setDetalhe(null); return; }
    setLoadingDetalhe(true);
    try {
      const res = await renegociacoesService.buscarPorId(r.id);
      setDetalhe(res.data);
    } catch (e) {
      setPageError(e?.message || "Falha ao carregar detalhes.");
    } finally { setLoadingDetalhe(false); }
  };

  const handleAbrirCancelar = (r) => {
    setMotivoCancel("");
    setShowCancelar(r);
  };
  const handleConfirmarCancelar = async () => {
    if (!motivoCancel.trim()) { setPageError("Informe o motivo do cancelamento."); return; }
    setCancelando(true);
    try {
      await renegociacoesService.cancelar(showCancelar.id, { motivoCancelamento: motivoCancel.trim() });
      setPageSuccess(`Acordo #${showCancelar.id} cancelado.`);
      setShowCancelar(null); setMotivoCancel("");
      setDetalhe(null);
      await Promise.all([carregarLista(), carregarStats()]);
    } catch (e) {
      setPageError(e?.message || "Falha ao cancelar.");
    } finally { setCancelando(false); }
  };

  const handleImprimirBoleto = async (idBoleto) => {
    try {
      const r = await cobrancasService.imprimirBoleto(idBoleto);
      const w = window.open("", "_blank", "width=820,height=900");
      w.document.write(r.html); w.document.close();
    } catch (e) {
      setPageError(e?.message || "Falha ao gerar boleto.");
    }
  };

  const handleImprimirAcordo = async (id) => {
    try {
      const r = await renegociacoesService.imprimirAcordo(id);
      const w = window.open("", "_blank", "width=900,height=1000");
      w.document.write(r.html); w.document.close();
    } catch (e) {
      setPageError(e?.message || "Falha ao gerar o termo de acordo.");
    }
  };

  const unidadeSel  = unidades.find(u => String(u.id) === String(idUnidadeSel));
  const totalSel    = boletosDisp.filter(b => boletosSel.includes(b.id));
  const vlrTotalSel = totalSel.reduce((a, b) => a + parseFloat(b.vlrTotal || 0), 0);

  // Última parcela recebe o ajuste de centavos no preview rápido
  const nrParc      = Math.max(1, parseInt(formValores.nrParcelas || 1));
  const vlrAcordo   = Math.max(0, vlrTotalSel - parseFloat(formValores.desconto || 0));
  const vlrParcBase = parseFloat((vlrAcordo / nrParc).toFixed(2));
  const diffCents   = parseFloat((vlrAcordo - vlrParcBase * nrParc).toFixed(2));
  const vlrUltima   = parseFloat((vlrParcBase + diffCents).toFixed(2));

  return (
    <div>
      <Banner tipo="error"   onClose={() => setPageError("")}>{pageError}</Banner>
      <Banner tipo="success" onClose={() => setPageSuccess("")}>{pageSuccess}</Banner>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-arrow-repeat me-2" style={{ color: "#7C3AED", fontSize: 22 }} />
            Renegociação
          </h1>
          <p className="page-subtitle">Acordo de parcelamento de dívidas inadimplentes</p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          {aba === "lista" && canEdit && (
            <button className="btn btn-primary" onClick={() => { setAba("novo"); setStep(0); setPageError(""); }} disabled={!idCondo}>
              <i className="bi bi-plus-lg" /> Nova Renegociação
            </button>
          )}
          {aba === "novo" && (
            <button className="btn btn-outline-secondary" onClick={() => setAba("lista")}>
              <i className="bi bi-arrow-left" /> Voltar
            </button>
          )}
        </div>
      </div>

      {/* Cards de resumo (visíveis na lista) */}
      {aba === "lista" && stats && idCondo && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, marginBottom: 14 }}>
          <StatCard label="Total"      value={stats.total || 0}      color="#7C3AED" bg="#FAF5FF" />
          <StatCard label="Ativos"     value={stats.ativos || 0}     color="#1E40AF" bg="#DBEAFE" hint={fmtM(stats.vlrAtivo)} />
          <StatCard label="Quitados"   value={stats.quitados || 0}   color="#166534" bg="#DCFCE7" hint={fmtM(stats.vlrQuitado)} />
          <StatCard label="Quebrados"  value={stats.quebrados || 0}  color="#991B1B" bg="#FEE2E2" />
          <StatCard label="Cancelados" value={stats.cancelados || 0} color="#475569" bg="#F1F5F9" />
        </div>
      )}

      {/* ── WIZARD DE CRIAÇÃO ── */}
      {aba === "novo" && (
        <div className="card">
          {/* Steps */}
          <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--gray-100)", background: "var(--gray-50)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
              {STEPS.map((s, i) => (
                <React.Fragment key={i}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 13, fontWeight: 700,
                      background: i <= step ? "#7C3AED" : "var(--gray-200)",
                      color: i <= step ? "#fff" : "var(--gray-500)",
                    }}>
                      {i < step ? <i className="bi bi-check" /> : i + 1}
                    </div>
                    <span style={{ fontSize: 12.5, fontWeight: i === step ? 600 : 400, color: i <= step ? "#7C3AED" : "var(--gray-400)" }}>{s}</span>
                  </div>
                  {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, background: i < step ? "#7C3AED" : "var(--gray-200)", margin: "0 8px" }} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div style={{ padding: 24 }}>

            {/* STEP 0 — Seleciona unidade */}
            {step === 0 && (
              <div className="form-section">
                <div className="form-section-title"><i className="bi bi-house-door" /> Selecione a Unidade</div>
                <div className="row g-3 align-items-end">
                  <div className="col-md-5">
                    <label className="form-label">Unidade <span className="required-mark">*</span></label>
                    <select className="form-select" value={idUnidadeSel} onChange={e => setIdUnidadeSel(e.target.value)}>
                      <option value="">— Selecione —</option>
                      {unidades.map(u => (
                        <option key={u.id} value={u.id}>
                          {u.bloco ? `Bl.${u.bloco} - ` : ""}{u.unidade}
                          {u.nmCondomino ? ` — ${u.nmCondomino}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <button className="btn btn-primary w-100" onClick={() => { carregarBoletos(); setStep(1); }} disabled={!idUnidadeSel || loadingBoletos}>
                      {loadingBoletos
                        ? <><span className="spinner-border spinner-border-sm me-2" />Buscando...</>
                        : <><i className="bi bi-search me-2" />Buscar Boletos Vencidos</>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 1 — Seleciona boletos */}
            {step === 1 && (
              <div className="form-section">
                <div className="form-section-title d-flex justify-content-between align-items-center">
                  <span><i className="bi bi-receipt" /> Boletos Vencidos — {unidadeSel?.unidade}{unidadeSel?.bloco ? `/Bl.${unidadeSel.bloco}` : ""}</span>
                  {boletosDisp.length > 0 && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={toggleTodos} style={{ fontSize: 12 }}>
                      {boletosSel.length === boletosDisp.length ? "Desmarcar Todos" : "Selecionar Todos"}
                    </button>
                  )}
                </div>

                {boletosDisp.length === 0
                  ? <div style={{ textAlign: "center", padding: "32px 0", color: "var(--gray-400)" }}>
                      <i className="bi bi-check-circle" style={{ fontSize: 36, display: "block", marginBottom: 8, color: "#166534" }} />
                      <p>Nenhum boleto vencido disponível para esta unidade.</p>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => setStep(0)}><i className="bi bi-arrow-left me-1" />Voltar</button>
                    </div>
                  : <>
                    <table className="table table-custom">
                      <thead><tr>
                        <th style={{ width: 40 }}><input type="checkbox" checked={boletosSel.length === boletosDisp.length} onChange={toggleTodos} /></th>
                        <th>Competência</th><th>Vencimento</th><th>Dias Atraso</th>
                        <th style={{ textAlign: "right" }}>Valor Original</th>
                        <th style={{ textAlign: "right" }}>Multa</th>
                        <th style={{ textAlign: "right" }}>Juros</th>
                        <th style={{ textAlign: "right", fontWeight: 700 }}>Total Atualizado</th>
                      </tr></thead>
                      <tbody>
                        {boletosDisp.map(b => (
                          <tr key={b.id} style={{ background: boletosSel.includes(b.id) ? "#FAF5FF" : "" }}>
                            <td><input type="checkbox" checked={boletosSel.includes(b.id)} onChange={() => toggleBoleto(b.id)} /></td>
                            <td style={{ fontSize: 12.5 }}>{b.competencia || "—"}</td>
                            <td style={{ fontSize: 12.5 }}>{fmtD(b.dtVencimento)}</td>
                            <td>
                              <span style={{ background:"var(--bg-danger-soft)", color: "#991B1B", padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                {b.diasAtraso}d
                              </span>
                            </td>
                            <td style={{ textAlign: "right", fontSize: 12.5 }}>{fmtM(b.vlrBoleto)}</td>
                            <td style={{ textAlign: "right", fontSize: 12.5, color: "#C2410C" }}>{fmtM(b.vlrMulta)}</td>
                            <td style={{ textAlign: "right", fontSize: 12.5, color: "#C2410C" }}>{fmtM(b.vlrJuros)}</td>
                            <td style={{ textAlign: "right", fontWeight: 700, color: "#991B1B" }}>{fmtM(b.vlrTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                      {boletosSel.length > 0 && (
                        <tfoot>
                          <tr style={{ fontWeight: 700, background: "#FAF5FF" }}>
                            <td colSpan={4} style={{ fontSize: 13 }}>SELECIONADOS ({boletosSel.length})</td>
                            <td style={{ textAlign: "right" }}>{fmtM(totalSel.reduce((a, b) => a + parseFloat(b.vlrBoleto || 0), 0))}</td>
                            <td style={{ textAlign: "right", color: "#C2410C" }}>{fmtM(totalSel.reduce((a, b) => a + parseFloat(b.vlrMulta || 0), 0))}</td>
                            <td style={{ textAlign: "right", color: "#C2410C" }}>{fmtM(totalSel.reduce((a, b) => a + parseFloat(b.vlrJuros || 0), 0))}</td>
                            <td style={{ textAlign: "right", color: "#7C3AED", fontSize: 14 }}>{fmtM(vlrTotalSel)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                    <div className="d-flex justify-content-between mt-2">
                      <button className="btn btn-outline-secondary" onClick={() => setStep(0)}><i className="bi bi-arrow-left" /> Voltar</button>
                      <button className="btn btn-primary" onClick={() => setStep(2)} disabled={!boletosSel.length}>
                        Próximo <i className="bi bi-arrow-right" />
                      </button>
                    </div>
                  </>
                }
              </div>
            )}

            {/* STEP 2 — Define valores do acordo */}
            {step === 2 && (
              <div className="form-section">
                <div className="form-section-title"><i className="bi bi-sliders" /> Condições do Acordo</div>

                <div style={{ background: "#FAF5FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "12px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 13, color: "#7C3AED", fontWeight: 600 }}>
                    {boletosSel.length} boleto(s) selecionado(s) — Total da dívida: <strong>{fmtM(vlrTotalSel)}</strong>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-3">
                    <label className="form-label">Desconto (R$)</label>
                    <div className="input-group">
                      <span className="input-group-text">R$</span>
                      <input className="form-control" type="number" step="0.01" min="0"
                        value={formValores.desconto} onChange={e => setFormValores(p => ({ ...p, desconto: e.target.value }))} />
                    </div>
                    <small style={{ fontSize: 11, color: "var(--gray-400)" }}>Desconto sobre multa/juros</small>
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">Nº Parcelas <span className="required-mark">*</span></label>
                    <input className="form-control" type="number" min="1" max="60"
                      value={formValores.nrParcelas} onChange={e => setFormValores(p => ({ ...p, nrParcelas: e.target.value }))} />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Vencimento 1ª Parcela <span className="required-mark">*</span></label>
                    <input type="date" className="form-control"
                      value={formValores.dtPrimeiraParcela} onChange={e => setFormValores(p => ({ ...p, dtPrimeiraParcela: e.target.value }))} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Conta Bancária</label>
                    <select className="form-select" value={formValores.idContaBancaria} onChange={e => setFormValores(p => ({ ...p, idContaBancaria: e.target.value }))}>
                      <option value="">— Selecione —</option>
                      {contas.map(c => <option key={c.id} value={c.id}>{c.nmConta} — {c.banco || ""}</option>)}
                    </select>
                  </div>

                  {/* Preview rápido do valor */}
                  {nrParc > 0 && vlrAcordo > 0 && (
                    <div className="col-12">
                      <div style={{ background: "#FAF5FF", borderRadius: 8, padding: "10px 16px", display: "flex", gap: 20, flexWrap: "wrap", fontSize: 13 }}>
                        <span>Total acordado: <strong style={{ color: "#7C3AED" }}>{fmtM(vlrAcordo)}</strong></span>
                        <span>Parcela: <strong style={{ color: "#7C3AED" }}>{fmtM(vlrParcBase)}</strong>{nrParc > 1 && diffCents !== 0 && <small style={{ color:"var(--gray-500)", marginLeft: 6 }}>(última: {fmtM(vlrUltima)})</small>}</span>
                      </div>
                    </div>
                  )}

                  <div className="col-12">
                    <label className="form-label">Observações</label>
                    <textarea className="form-control" rows={2} value={formValores.observacao}
                      onChange={e => setFormValores(p => ({ ...p, observacao: e.target.value }))}
                      placeholder="Condições especiais do acordo..." />
                  </div>
                </div>

                <div className="d-flex justify-content-between mt-3">
                  <button className="btn btn-outline-secondary" onClick={() => setStep(1)}><i className="bi bi-arrow-left" /> Voltar</button>
                  <button className="btn btn-primary" onClick={handleSimular} disabled={loadingSim || !formValores.dtPrimeiraParcela}>
                    {loadingSim
                      ? <><span className="spinner-border spinner-border-sm me-2" />Simulando...</>
                      : <><i className="bi bi-calculator me-2" />Simular Acordo</>}
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3 — Preview da simulação */}
            {step === 3 && simulacao && (
              <div className="form-section">
                <div className="form-section-title"><i className="bi bi-table" /> Preview do Acordo</div>

                {/* Resumo financeiro */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 }}>
                  {[
                    { label: "Dívida Original", val: simulacao.vlrOriginal, color: "#991B1B", bg: "#FEE2E2" },
                    { label: "Multa + Juros",   val: parseFloat(simulacao.vlrMulta) + parseFloat(simulacao.vlrJuros), color: "#C2410C", bg: "#FFF7ED" },
                    { label: "Desconto",        val: simulacao.vlrDesconto, color: "#166534", bg: "#DCFCE7" },
                    { label: "Total do Acordo", val: simulacao.vlrTotal,    color: "#7C3AED", bg: "#FAF5FF" },
                  ].map(s => (
                    <div key={s.label} style={{ background: s.bg, borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{fmtM(s.val)}</div>
                      <div style={{ fontSize: 11, color: s.color, opacity: 0.8 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Parcelas */}
                <div style={{ fontWeight: 600, fontSize: 13, color: "#7C3AED", marginBottom: 8 }}>
                  {simulacao.parcelas.length} Parcela(s) — Boletos gerados automaticamente ao confirmar
                </div>
                <table className="table table-custom">
                  <thead><tr>
                    <th>Parcela</th><th>Vencimento</th><th style={{ textAlign: "right" }}>Valor</th>
                  </tr></thead>
                  <tbody>
                    {simulacao.parcelas.map(p => (
                      <tr key={p.nrParcela}>
                        <td style={{ fontWeight: 600, color: "#7C3AED" }}>{p.nrParcela}/{simulacao.parcelas.length}</td>
                        <td>{fmtD(p.dtVencimento)}</td>
                        <td style={{ textAlign: "right", fontWeight: 700 }}>{fmtM(p.vlrParcela)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 8, padding: "10px 14px", fontSize: 12.5, color: "#C2410C", marginBottom: 16 }}>
                  <i className="bi bi-exclamation-triangle me-2" />
                  Ao confirmar: os {boletosSel.length} boleto(s) originais serão <strong>cancelados</strong> e {simulacao.parcelas.length} novo(s) boleto(s) de parcela serão <strong>gerados automaticamente</strong>.
                  O condômino receberá uma notificação por e-mail e WhatsApp (se cadastrados).
                </div>

                <div className="d-flex justify-content-between">
                  <button className="btn btn-outline-secondary" onClick={() => setStep(2)}><i className="bi bi-arrow-left" /> Voltar</button>
                  <button className="btn btn-success btn-lg" onClick={handleConfirmar} disabled={confirmando}>
                    {confirmando
                      ? <><span className="spinner-border spinner-border-sm me-2" />Criando acordo...</>
                      : <><i className="bi bi-check2-circle me-2" />Confirmar Renegociação</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── LISTA DE ACORDOS ── */}
      {aba === "lista" && (
        <div className="card">
          {/* Filtros */}
          <div style={{ padding: "10px 16px", borderBottom: "1px solid var(--gray-100)", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <select className="form-select form-select-sm" style={{ width: "auto" }} value={filtros.status} onChange={e => setFiltros(p => ({ ...p, status: e.target.value }))}>
              <option value="">Todos os status</option>
              {Object.entries(STATUS_RENEG).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: "auto" }} value={filtros.idUnidade} onChange={e => setFiltros(p => ({ ...p, idUnidade: e.target.value }))}>
              <option value="">Todas as unidades</option>
              {unidades.map(u => <option key={u.id} value={u.id}>{u.bloco ? `Bl.${u.bloco}-` : ""}{u.unidade}{u.nmCondomino ? ` — ${u.nmCondomino}` : ""}</option>)}
            </select>
            {(filtros.status || filtros.idUnidade) && (
              <button className="btn btn-outline-secondary btn-sm" onClick={() => setFiltros({ status: "", idUnidade: "" })}>
                <i className="bi bi-x-circle me-1" />Limpar
              </button>
            )}
            <button className="btn btn-outline-secondary btn-sm ms-auto" onClick={() => { carregarLista(); carregarStats(); }}><i className="bi bi-arrow-clockwise" /></button>
          </div>

          {/* Tabela */}
          <div className="table-responsive">
            <table className="table table-custom">
              <thead><tr>
                <th>Unidade</th><th>Condômino</th>
                <th style={{ textAlign: "right" }}>Total Acordo</th>
                <th>Parcelas</th>
                <th style={{ textAlign: "right" }}>Valor Parcela</th>
                <th>1ª Parcela</th>
                <th>Status</th><th>Criado em</th>
                <th style={{ width: 140 }}>Ações</th>
              </tr></thead>
              <tbody>
                {loadingLista
                  ? <tr><td colSpan={9} className="text-center py-4"><span className="spinner-border spinner-border-sm text-primary" /></td></tr>
                  : !idCondo
                  ? <tr><td colSpan={9} className="text-center py-4" style={{ color: "var(--gray-400)" }}>Selecione um condomínio</td></tr>
                  : lista.length === 0
                  ? <tr><td colSpan={9} className="text-center py-4" style={{ color: "var(--gray-400)" }}>
                      <i className="bi bi-arrow-repeat" style={{ fontSize: 32, display: "block", marginBottom: 8, color: "#7C3AED" }} />
                      Nenhuma renegociação encontrada{(filtros.status || filtros.idUnidade) ? " com os filtros aplicados" : ""}.
                    </td></tr>
                  : lista.map(r => (
                    <React.Fragment key={r.id}>
                      <tr style={{ background: detalhe?.id === r.id ? "#FAF5FF" : "" }}>
                        <td style={{ fontWeight: 700, color: "#7C3AED" }}>{r.unidade}{r.bloco ? `/Bl.${r.bloco}` : ""}</td>
                        <td style={{ fontSize: 12.5 }}>{r.nmCondomino || "—"}</td>
                        <td style={{ textAlign: "right", fontWeight: 700 }}>{fmtM(r.vlrTotal)}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ flex: 1, height: 6, background: "var(--gray-100)", borderRadius: 3, overflow: "hidden", minWidth: 60 }}>
                              <div style={{ height: "100%", background: "#7C3AED", width: `${r.qtdParcTotal > 0 ? (r.qtdParcPagas / r.qtdParcTotal) * 100 : 0}%`, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 12, color: "var(--gray-500)" }}>{r.qtdParcPagas}/{r.qtdParcTotal}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: "right", fontSize: 12.5 }}>{fmtM(r.vlrParcela)}</td>
                        <td style={{ fontSize: 12.5 }}>{fmtD(r.dtPrimeiraParcela)}</td>
                        <td>{(() => { const s = STATUS_RENEG[r.status] || STATUS_RENEG.Ativo; return (<span style={{ ...s, padding: "2px 9px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{s.label}</span>); })()}</td>
                        <td style={{ fontSize: 12 }}>{fmtD(r.createdAt)}</td>
                        <td>
                          <div className="table-actions">
                            <button className="btn btn-outline-secondary btn-sm btn-icon" title="Ver parcelas" onClick={() => handleVerDetalhe(r)}>
                              <i className={`bi bi-chevron-${detalhe?.id === r.id ? "up" : "down"}`} />
                            </button>
                            <button className="btn btn-outline-primary btn-sm btn-icon" title="Imprimir termo de acordo" onClick={() => handleImprimirAcordo(r.id)}>
                              <i className="bi bi-file-earmark-text" />
                            </button>
                            {canEdit && r.status === "Ativo" && (
                              <button className="btn btn-outline-danger btn-sm btn-icon" title="Cancelar" onClick={() => handleAbrirCancelar(r)}>
                                <i className="bi bi-x-circle" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Detalhe das parcelas + boletos originais */}
                      {detalhe?.id === r.id && (
                        <tr>
                          <td colSpan={9} style={{ padding: "0 16px 12px 32px", background: "#FAF5FF" }}>
                            {loadingDetalhe
                              ? <div className="text-center py-3"><span className="spinner-border spinner-border-sm text-primary" /></div>
                              : <>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#7C3AED", padding: "8px 0 6px" }}>Parcelas do Acordo</div>
                                <table style={{ width: "100%", fontSize: 12 }}>
                                  <thead><tr style={{ color: "var(--gray-500)", fontWeight: 600 }}>
                                    <th style={{ padding: "4px 8px" }}>Parcela</th>
                                    <th style={{ padding: "4px 8px" }}>Vencimento</th>
                                    <th style={{ padding: "4px 8px", textAlign: "right" }}>Valor</th>
                                    <th style={{ padding: "4px 8px" }}>Status</th>
                                    <th style={{ padding: "4px 8px" }}>Nosso Número</th>
                                    <th style={{ padding: "4px 8px" }}>Ação</th>
                                  </tr></thead>
                                  <tbody>
                                    {detalhe.parcelas?.map(p => (
                                      <tr key={p.id}>
                                        <td style={{ padding: "4px 8px", fontWeight: 600, color: "#7C3AED" }}>{p.nrParcela}/{detalhe.nrParcelas}</td>
                                        <td style={{ padding: "4px 8px" }}>{fmtD(p.dtVencimento)}</td>
                                        <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600 }}>{fmtM(p.vlrParcela)}</td>
                                        <td style={{ padding: "4px 8px" }}>
                                          {(() => { const s = STATUS_PARC[p.status] || STATUS_PARC.Aberto; return (<span style={{ ...s, padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{p.status}</span>); })()}
                                        </td>
                                        <td style={{ padding: "4px 8px", fontFamily: "monospace", fontSize: 11 }}>{p.nossoNumero || "—"}</td>
                                        <td style={{ padding: "4px 8px" }}>
                                          {p.idBoletoGerado && (
                                            <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11, padding: "2px 8px" }} onClick={() => handleImprimirBoleto(p.idBoletoGerado)}>
                                              <i className="bi bi-printer me-1" />Imprimir
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                {/* Boletos originais — com info completa */}
                                {detalhe.itens?.length > 0 && (
                                  <div style={{ marginTop: 12 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: "#991B1B", marginBottom: 4 }}>
                                      Boletos originais cancelados ({detalhe.itens.length})
                                    </div>
                                    <table style={{ width: "100%", fontSize: 11.5, background:"var(--bg-danger-soft)", borderRadius: 6 }}>
                                      <thead><tr style={{ color: "#991B1B" }}>
                                        <th style={{ padding: "4px 8px", textAlign: "left" }}>Nosso Nº</th>
                                        <th style={{ padding: "4px 8px", textAlign: "left" }}>Competência</th>
                                        <th style={{ padding: "4px 8px", textAlign: "left" }}>Vencimento Original</th>
                                        <th style={{ padding: "4px 8px", textAlign: "right" }}>Valor</th>
                                        <th style={{ padding: "4px 8px", textAlign: "right" }}>Multa</th>
                                        <th style={{ padding: "4px 8px", textAlign: "right" }}>Juros</th>
                                        <th style={{ padding: "4px 8px", textAlign: "center" }}>Atraso</th>
                                      </tr></thead>
                                      <tbody>
                                        {detalhe.itens.map(i => (
                                          <tr key={i.id}>
                                            <td style={{ padding: "4px 8px", fontFamily: "monospace" }}>{i.nossoNumero || "—"}</td>
                                            <td style={{ padding: "4px 8px" }}>{i.competencia || "—"}</td>
                                            <td style={{ padding: "4px 8px" }}>{fmtD(i.dtVencOriginal)}</td>
                                            <td style={{ padding: "4px 8px", textAlign: "right" }}>{fmtM(i.vlrOriginal)}</td>
                                            <td style={{ padding: "4px 8px", textAlign: "right" }}>{fmtM(i.vlrMulta)}</td>
                                            <td style={{ padding: "4px 8px", textAlign: "right" }}>{fmtM(i.vlrJuros)}</td>
                                            <td style={{ padding: "4px 8px", textAlign: "center" }}>{i.diasAtraso}d</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}

                                {detalhe.motivoCancelamento && (
                                  <div style={{ marginTop: 10, padding: "6px 10px", background:"var(--bg-page)", borderRadius: 6, fontSize: 11.5 }}>
                                    <strong>Motivo do cancelamento:</strong> {detalhe.motivoCancelamento}
                                  </div>
                                )}
                              </>}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── MODAL DE CANCELAMENTO ── */}
      {showCancelar && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-exclamation-triangle text-warning me-2" />Cancelar Acordo #{showCancelar.id}</h5>
                <button type="button" className="btn-close" onClick={() => !cancelando && setShowCancelar(null)} />
              </div>
              <div className="modal-body">
                <Banner tipo="warning">
                  Esta ação cancelará o acordo e <strong>todos os boletos das parcelas em aberto</strong>.
                  Não é possível reverter.
                </Banner>
                <label className="form-label">Motivo do cancelamento <span className="required-mark">*</span></label>
                <textarea
                  className="form-control" rows={3}
                  value={motivoCancel}
                  onChange={e => setMotivoCancel(e.target.value)}
                  placeholder="Ex.: solicitação do condômino, erro de digitação, etc."
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowCancelar(null)} disabled={cancelando}>
                  Voltar
                </button>
                <button className="btn btn-danger" onClick={handleConfirmarCancelar} disabled={cancelando || !motivoCancel.trim()}>
                  {cancelando
                    ? <><span className="spinner-border spinner-border-sm me-2" />Cancelando...</>
                    : <><i className="bi bi-x-circle me-2" />Confirmar Cancelamento</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
